<template>
    <v-app id="app">
        <!-- App bar -->
        <v-app-bar app clipped-right color="bal-gray" light flat v-if="user.id">
            <v-app-bar-nav-icon large @click.stop="toggleDrawer" />
            <v-toolbar-title class="mr-2">
                <PageHeaderTitle
                    :page-title="pageTitle"
                    :breadcrumbs="breadcrumbs"
                    :loading="loadingWorkOrders || loadingWorkOrderData"
                />
            </v-toolbar-title>

            <v-combobox
                v-if="
                    isListView && hasPermissionForAction(PermissionsConstants.WORK_ORDER, PermissionsConstants.SEARCH)
                "
                :items="searchHistory"
                :value="searchWorkOrderString"
                @input="controlSearchString($event)"
                @click:clear="handleClearClick"
                item-text="keyword"
                id="searchInput"
                class="mt-6 mx-2"
                :placeholder="t('Work order production order number, production date')"
                prepend-inner-icon="search"
                flat
                solo
                dense
                clearable
            >
                <template slot="item" slot-scope="data">
                    <span
                        @click="updateWorkOrderSearchOnClick(data.item.keyword)"
                        style="width: 100%; height: 100%"
                        class="pa-2"
                        >{{ data.item.keyword }}</span
                    >
                </template>
            </v-combobox>
            <v-spacer v-else />

            <v-btn
                dark
                color="bal-dark-gray"
                v-if="isListView"
                class="mr-2 px-2"
                @click.stop="toggleListSettingsModal"
                style="min-width: 0"
            >
                <v-badge color="error" offset-x="0" offset-y="2" :value="showFilterNotification">
                    <v-icon>settings</v-icon>
                </v-badge>
            </v-btn>

            <v-btn
                @click="handleForcePullFromOdooClick"
                dark
                color="bal-red"
                class="mr-2"
                v-if="isListView && user.work_roles && user.work_roles.includes('superadmin')"
            >
                <v-icon>add</v-icon>
                {{ "Force pull from Odoo" | t }}
            </v-btn>
            <v-btn
                @click="handleAddNewWorkOrderGroupClick"
                dark
                color="bal-red"
                v-if="isListView && user.role === PermissionsConstants.ROLE_SUPERADMIN"
            >
                <v-icon>done</v-icon>
                {{ "Add new group" | t }}
            </v-btn>
            <v-btn
                @click="handleSaveClick(false)"
                dark
                color="bal-red"
                v-if="
                    $route.name === 'work-order-view' &&
                    user.work_roles &&
                    user.work_roles.length > 0 &&
                    ((hasPermissionForAction(PermissionsConstants.WORK_ORDER, PermissionsConstants.EDIT) &&
                        user.work_roles.includes(PermissionsConstants.ROLE_HEAD_OF_SHIFT)) ||
                        user.work_roles.includes(PermissionsConstants.ROLE_OPERATOR) ||
                        user.role === PermissionsConstants.ROLE_SUPERADMIN)
                "
            >
                <v-icon>done</v-icon>
                {{ "Save" | t }}
            </v-btn>
            <v-menu
                offset-y
                bottom
                left
                v-if="
                    $route.name === 'work-order-view' &&
                    user &&
                    user.role === PermissionsConstants.ROLE_SUPERADMIN &&
                    activeWorkOrder.ext_id
                "
            >
                <template v-slot:activator="{ on }">
                    <v-btn color="bal-dark-gray" class="px-2 ml-2 mx-2" style="min-width: 0" dark v-on="on">
                        <v-icon>expand_more</v-icon>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item @click="handleWorkOrderGroupClick">
                        <v-list-item-title>{{ "Work Order group" | t }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="handleOpenFromOdooClick">
                        <v-list-item-title>{{ "Open from ODOO" | t }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="handleSaveClick(true)">
                        <v-list-item-title>{{ "Save without ODOO sending" | t }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-app-bar>

        <v-overlay :value="loadingWorkOrders">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-overlay>
        <!-- Drawer left -->
        <main-drawer :drawer-open="mainDrawerOpen" @close="toggleDrawer" />

        <!-- Main content -->
        <v-main>
            <router-view />
        </v-main>

        <force-pull-from-odoo-modal :showModal="forcePullFromOdooModalVisible"></force-pull-from-odoo-modal>

        <work-order-list-settings-modal />

        <work-order-duration-confirmation-modal
            @confirm-duration="resolveDurationConfirmation"
            @reject-duration="resolveDurationConfirmation"
        />

        <!-- Work order group drawer -->
        <v-navigation-drawer :value="workOrderGroupDrawer" fixed right hide-overlay width="900">
            <work-order-group-view
                v-if="['work-order-group-view', 'work-order-view-group'].includes($route.name)"
                :stayToWorkOrder="['work-order-view-group'].includes($route.name)"
            ></work-order-group-view>
            <work-order-group-edit v-if="$route.name === 'work-order-group-edit'"></work-order-group-edit>
        </v-navigation-drawer>

        <v-snackbar color="success" bottom :timeout="2500" :value="requestSuccess">
            <div style="display: flex; flex-direction: row">
                <v-icon color="#FFF">check</v-icon>
                <div class="title pa-2">{{ "Success" | t }}</div>
            </div>
        </v-snackbar>

        <!-- Error snackbar -->
        <v-snackbar color="error" bottom :timeout="2500" :value="requestError">
            <div style="display: flex; flex-direction: row">
                <v-icon color="#FFF">error</v-icon>
                <div class="title pa-2">{{ errorMessage }}</div>
            </div>
        </v-snackbar>

        <!-- Footer -->
        <page-footer />
    </v-app>
</template>
<script>
import MainDrawer from "../components/main-drawer.vue";
import PageFooter from "../components/page-footer.vue";
import SalesInvoicesView from "../pages/sales-invoices/sales-invoices-view";
import WorkOrderGroupView from "@/pages/work-order/work-order-group-view.vue";
import WorkOrderGroupEdit from "@/pages/work-order/work-order-group-edit.vue";
import { PermissionsConstants } from "@/permissions";
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
import ForcePullFromOdooModal from "@/pages/work-order/modals/force-pull-from-odoo-modal.vue";
import WorkOrderListSettingsModal from "@/pages/work-order/modals/work-order-list-settings-modal.vue";
import WorkOrderDurationConfirmationModal from "../pages/work-order/modals/work-order-duration-confirmation-modal.vue";
import { workOrderMixin } from "@/mixins/work-order-mixin";
import PageHeaderTitle from "@/components/dropdown/page-header-title.vue";

export default {
    components: {
        PageHeaderTitle,
        WorkOrderGroupEdit,
        WorkOrderGroupView,
        SalesInvoicesView,
        PageFooter,
        MainDrawer,
        ForcePullFromOdooModal,
        WorkOrderListSettingsModal,
        WorkOrderDurationConfirmationModal,
    },
    mixins: [workOrderMixin],
    data() {
        return {
            mainDrawerOpen: null,
            showFilterNotification: false,
        };
    },

    watch: {
        requestSuccess: function (value) {
            if (value) {
                setTimeout(() => {
                    this.setRequestSuccess(false);
                }, 2500);
            }
        },
        requestError: function (value) {
            if (value)
                setTimeout(() => {
                    this.setRequestError(false);
                    this.setErrorMessage("");
                }, 2500);
        },
        statusForFiltering() {
            this.showFilterNotification = false;
            try {
                if (this.statusForFiltering.length !== this.statuses.length) {
                    this.showFilterNotification = true;
                }
            } catch (e) {
                console.log(e);
            }
        },
    },
    mounted() {
        document.addEventListener("keydown", this.handleKeyboardListener);

        if (this.isListView) {
            const searchTerm = this.$route.query.search || "";
            this.updateWorkOrderSearch({ term: searchTerm });
        }

        const drawerState = localStorage.getItem("mainDrawerOpen");
        if (drawerState) {
            this.mainDrawerOpen = JSON.parse(drawerState);
        } else {
            this.mainDrawerOpen = true;
        }
    },
    destroyed() {
        if (this.searchWorkOrderString.length > 0) this.resetWorkOrderSearch();
        document.removeEventListener("keydown", this.handleKeyboardListener);
        this.setRequestSuccess(false);
        this.setErrorMessage("");
    },
    methods: {
        ...mapActions("WorkOrderStore", ["saveWorkOrder", "getWorkOrder", "fetchWorkOrdersFromOdoo"]),
        ...mapActions("SearchHistoryStore", ["getSearchHistory"]),
        ...mapMutations("WorkOrderStore", [
            "resetActiveWorkOrderGroup",
            "setRequestSuccess",
            "setRequestError",
            "setErrorMessage",
            "setForcePullFromOdooModalVisible",
            "toggleListSettingsModal",
            "updateWorkOrderSearch",
            "resetWorkOrderSearch",
            "setDurationConfirmationModal",
            "setDurationConfirmationMessage",
        ]),
        toggleDrawer() {
            this.mainDrawerOpen = !this.mainDrawerOpen;
            localStorage.setItem("mainDrawerOpen", this.mainDrawerOpen);
        },

        async handleSaveClick(ignoreOdoo = false) {
            if (this.activeWorkOrder.status === "confirmed" && !ignoreOdoo) {
                const durationValidated = await this.validateDuration();
                if (!durationValidated) return;
            }
            await this.saveWorkOrder({ workOrderId: this.$route.params.workOrderId, ignoreOdoo });
            await this.getWorkOrder(this.$route.params.workOrderId);
        },
        async validateDuration() {
            const durationData = workOrderMixin.methods.calculateActualDuration(
                this.activeWorkOrder.production_date,
                this.activeWorkOrder.exchange_report_start_time,
                this.activeWorkOrder.exchange_report_end_time,
                true
            );
            const actualDuration = durationData?.actual_duration;

            if (actualDuration === null || actualDuration > 720) {
                const modalMessage =
                    actualDuration === null
                        ? "Unable to calculate the calculated actual duration. Would you like to continue and save anyway?"
                        : "The calculated actual duration exceeds 12 hours. Are you sure you want to proceed with saving?";

                const translatedMessage = this.t(modalMessage);

                return this.waitForDurationConfirmation(translatedMessage);
            }

            return true;
        },
        waitForDurationConfirmation(modalMessage) {
            this.setDurationConfirmationModal(true);
            this.setDurationConfirmationMessage(modalMessage);
            return new Promise((resolve) => {
                this.resolveModal = resolve;
            });
        },
        resolveDurationConfirmation(emittedBool) {
            this.setDurationConfirmationModal(false);
            this.setDurationConfirmationMessage(null);
            this.resolveModal(emittedBool);
        },
        handleWorkOrderGroupClick() {
            if (this.activeWorkOrder) {
                this.$router.push({
                    name: "work-order-view-group",
                    params: {
                        workOrderId: this.activeWorkOrder.id,
                        workOrderGroupId: this.activeWorkOrder.work_order_group_id,
                    },
                    query: {
                        tab: "Work orders",
                    },
                });
            }
        },
        handleAddNewWorkOrderGroupClick() {
            this.resetActiveWorkOrderGroup();
            this.$router.push({ name: "work-order-group-edit", params: { workOrderGroupId: "new" } });
        },
        async handleForcePullFromOdooClick() {
            this.setForcePullFromOdooModalVisible(true);
            await this.fetchWorkOrdersFromOdoo();
        },
        handleOpenFromOdooClick() {
            if (this.activeWorkOrder.ext_id) {
                window.open(
                    `https://balsnack.estpos.net/web#id=${this.activeWorkOrder.ext_id}&action=449&model=mrp.production&view_type=form&cids=1&menu_id=270`,
                    `_blank`
                );
            }
        },
        async controlSearchString(value) {
            if (value === "") {
                await this.getSearchHistory(this.$route.path.replace("/", ""));
                this.updateWorkOrderSearch({ term: "" });
                await this.$router.replace({ query: { ...this.$route.query, search: "" } });
            }
        },
        async handleClearClick() {
            await this.controlSearchString("");
        },

        async updateWorkOrderSearchOnClick(value) {
            this.updateWorkOrderSearch({ term: value });
            await this.$router.replace({ query: { ...this.$route.query, search: value } });
        },

        handleKeyboardListener(e) {
            if (e.keyCode === 13 && document.activeElement.tagName === "INPUT") {
                const val = document.querySelector("#searchInput").value;
                this.updateWorkOrderSearch({ term: val });
                this.$router.replace({ query: { ...this.$route.query, search: val } });
            }
        },
    },
    computed: {
        PermissionsConstants() {
            return PermissionsConstants;
        },
        ...mapState("WorkOrderStore", [
            "loadingWorkOrders",
            "loadingWorkOrderData",
            "drawerOpen",
            "workOrderGroupDrawer",
            "requestSuccess",
            "requestError",
            "errorMessage",
            "activeWorkOrder",
            "forcePullFromOdooModalVisible",
            "searchWorkOrderString",
            "listSettingsColumns",
            "statusForFiltering",
            "selectedLineNumberType",
            "workOrderLineNumberTypes",
            "workOrderLineNumbers",
            "activeWorkOrder",
        ]),
        ...mapState(["user"]),
        ...mapGetters(["hasPermissionForAction"]),
        ...mapState("SearchHistoryStore", ["searchHistory"]),
        isListView() {
            return this.$route.name === "work-order-list";
        },
        isSelectLineView() {
            return this.$route.name === "work-order-line-number-select";
        },
        pageTitle() {
            if (!this.isListView && this.activeWorkOrder && this.activeWorkOrder.line_number) {
                const translatedLineNumber = this.workOrderLineNumbers.find(
                    (x) => x.value === this.activeWorkOrder.line_number
                );
                return translatedLineNumber?.text || this.t("Work order");
            }
            const selectedType = this.workOrderLineNumberTypes.find(
                (type) => type.type === this.selectedLineNumberType
            );
            const prefix = selectedType ? `${this.t(selectedType.text)} - ` : "";
            return `${prefix}${this.t(this.$route.meta.title)}`;
        },
        breadcrumbs() {
            const route = this.$route;
            const metaCrumbs = route.meta.breadcrumbs || [];

            const groupTitle = () => {
                const selectedType = this.workOrderLineNumberTypes.find(
                    (type) => type.type === this.selectedLineNumberType
                );
                const prefix = selectedType ? `${this.t(selectedType.text)} - ` : "";
                return `${prefix}${this.t("Work orders")}`;
            };

            const labelMap = {
                "work-order-line-number-select": "Line numbers",
                "work-order-list": groupTitle(),
                "work-order-view-group": "Group",
                "work-order-view": this.activeWorkOrder.production_order_number ?? "Work order",
            };

            return metaCrumbs.map((crumb) => {
                let label = this.t(labelMap[crumb.name]) ?? this.t(crumb.label);

                let params = {};
                params.workOrderGroupId = this.activeWorkOrderGroup?.id || this.activeWorkOrder?.work_order_group_id;
                params.workOrderId = this.activeWorkOrder?.id;

                return {
                    text: label,
                    to: crumb.name ? { name: crumb.name, params, query: route.query } : null,
                };
            });
        },
    },
};
</script>

<style>
.v-input__icon--prepend-inner .v-icon {
    margin-top: 0px !important;
}
</style>
